import React, { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ServiceType } from "@/context/LeadContext";
import { useToast } from "@/components/ui/use-toast";
import { usePartners } from "@/context/PartnerContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { MapPin, Search, Mail, BellOff } from "lucide-react";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AddressAutocomplete from "@/components/ui-custom/AddressAutocomplete";
import ThankYouMessage from "@/components/ui-custom/ThankYouMessage";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const formSchema = z.object({
  name: z.string().min(3, {
    message: "Το όνομα πρέπει να είναι τουλάχιστον 3 χαρακτήρες",
  }),
  phone: z.string().min(10, {
    message: "Παρακαλώ εισάγετε έγκυρο αριθμό τηλεφώνου",
  }),
  email: z.string().trim().email({
    message: "Παρακαλώ εισάγετε έγκυρο email",
  }).or(z.literal("")),
  location: z.string().min(3, {
    message: "Παρακαλώ εισάγετε τοποθεσία",
  }),
  miniImageUrl: z.string().trim().max(500, {
    message: "Το URL εικόνας είναι πολύ μεγάλο",
  }).optional(),
  miniDescription: z.string().max(500, {
    message: "Η περιγραφή μπορεί να έχει έως 500 χαρακτήρες",
  }).optional(),
  services: z.array(z.string()).min(1, {
    message: "Επιλέξτε τουλάχιστον μία υπηρεσία",
  }),
  active: z.boolean().default(true),
  sms_opt_out: z.boolean().default(false), // Add the SMS opt-out field
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  radius: z.number().min(1).default(10),
});

type FormValues = z.infer<typeof formSchema>;

interface PartnerFormProps {
  onSuccess?: () => void;
  initialData?: {
    id?: string;
    name: string;
    phone: string;
    email: string;
    location: string;
    miniImageUrl?: string;
    miniDescription?: string;
    services: string[];
    active: boolean;
    sms_opt_out: boolean;
    latitude?: number;
    longitude?: number;
    radius?: number;
  };
  isAdminView?: boolean; // New prop to determine if we're in admin interface
}

const PartnerForm: React.FC<PartnerFormProps> = ({ onSuccess, initialData, isAdminView = false }) => {
  const { toast } = useToast();
  const { addPartner, updatePartner } = usePartners();
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  const defaultValues = {
    name: initialData?.name || "",
    phone: initialData?.phone || "",
    email: initialData?.email || "",
    location: initialData?.location || "",
    miniImageUrl: initialData?.miniImageUrl || "",
    miniDescription: initialData?.miniDescription || "",
    services: initialData?.services || [],
    active: initialData?.active ?? true,
    sms_opt_out: initialData?.sms_opt_out ?? false, // Initialize SMS opt-out field
    latitude: initialData?.latitude,
    longitude: initialData?.longitude,
    radius: initialData?.radius || 10,
  };
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    const initMaps = async () => {
      try {
        await loadGoogleMapsScript();
        setMapsLoaded(true);
        initializeMap();
      } catch (error) {
        console.error("Failed to load Google Maps:", error);
        toast({
          title: "Σφάλμα φόρτωσης χάρτη",
          description: "Δεν ήταν δυνατή η φόρτωση του χάρτη Google Maps.",
          variant: "destructive"
        });
      }
    };
    
    initMaps();
  }, [toast]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) return;
    
    const center = { lat: 38.9, lng: 22.9 };
    
    if (initialData?.latitude && initialData?.longitude) {
      center.lat = initialData.latitude;
      center.lng = initialData.longitude;
    }
    
    const mapOptions: google.maps.MapOptions = {
      center,
      zoom: initialData?.latitude ? 10 : 7,
      styles: [],
      mapTypeControl: false,
      streetViewControl: false
    };
    
    googleMapRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
    
    if (initialData?.latitude && initialData?.longitude) {
      const position = { lat: initialData.latitude, lng: initialData.longitude };
      
      const markerOptions: google.maps.MarkerOptions = {
        position,
        map: googleMapRef.current,
        draggable: true
      };
      
      markerRef.current = new window.google.maps.Marker(markerOptions);
      
      circleRef.current = new window.google.maps.Circle({
        strokeColor: "#1E88E5",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#1E88E5",
        fillOpacity: 0.1,
        map: googleMapRef.current,
        center: position,
        radius: (initialData.radius || 10) * 1000,
      });
      
      if (markerRef.current) {
        google.maps.event.addListener(markerRef.current, 'dragend', function() {
          const pos = markerRef.current!.getPosition();
          if (pos) {
            form.setValue('latitude', pos.lat());
            form.setValue('longitude', pos.lng());
            
            if (circleRef.current) {
              circleRef.current.setCenter(pos);
            }
          }
        });
      }
    }
    
    if (googleMapRef.current) {
      google.maps.event.addListener(googleMapRef.current, 'click', function(event) {
        placeMarker(event.latLng);
      });
    }
  };

  const placeMarker = (location: google.maps.LatLng) => {
    if (!window.google || !window.google.maps || !googleMapRef.current) return;
    
    form.setValue('latitude', location.lat());
    form.setValue('longitude', location.lng());
    
    if (markerRef.current) {
      markerRef.current.setPosition(location);
    } else {
      const markerOptions: google.maps.MarkerOptions = {
        position: location,
        map: googleMapRef.current,
        draggable: true
      };
      
      markerRef.current = new window.google.maps.Marker(markerOptions);
      
      if (markerRef.current) {
        google.maps.event.addListener(markerRef.current, 'dragend', function() {
          const pos = markerRef.current!.getPosition();
          if (pos) {
            form.setValue('latitude', pos.lat());
            form.setValue('longitude', pos.lng());
            
            if (circleRef.current) {
              circleRef.current.setCenter(pos);
            }
          }
        });
      }
    }
    
    const radiusInMeters = (form.getValues('radius') || 10) * 1000;
    
    if (circleRef.current) {
      circleRef.current.setCenter(location);
      circleRef.current.setRadius(radiusInMeters);
    } else {
      circleRef.current = new window.google.maps.Circle({
        strokeColor: "#1E88E5",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#1E88E5",
        fillOpacity: 0.1,
        map: googleMapRef.current,
        center: location,
        radius: radiusInMeters,
      });
    }
  };

  const updateRadius = (value: number[]) => {
    const radius = value[0];
    form.setValue('radius', radius);
    
    if (circleRef.current) {
      circleRef.current.setRadius(radius * 1000);
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    if (lat && lng) {
      form.setValue('latitude', lat);
      form.setValue('longitude', lng);
      
      const position = new google.maps.LatLng(lat, lng);
      
      if (googleMapRef.current) {
        googleMapRef.current.setCenter(position);
        googleMapRef.current.setZoom(12);
      }
      
      placeMarker(position);
    }
  };

  const onSubmit = async (data: FormValues) => {
    try {
      if (initialData?.id) {
        await updatePartner(initialData.id, {
          name: data.name,
          phone: data.phone,
          email: (data.email || "").trim() || "",
          location: data.location,
          miniImageUrl: (data.miniImageUrl || "").trim() || undefined,
          miniDescription: (data.miniDescription || "").trim() || undefined,
          services: data.services as ServiceType[],
          active: data.active,
          sms_opt_out: data.sms_opt_out, // Include SMS opt-out in update
          latitude: data.latitude,
          longitude: data.longitude,
          radius: data.radius
        });
        toast({
          title: "Επιτυχής ενημέρωση!",
          description: "Τα στοιχεία του συνεργάτη ενημερώθηκαν επιτυχώς.",
        });
      } else {
        await addPartner({
          name: data.name,
          phone: data.phone,
          email: (data.email || "").trim() || "",
          location: data.location,
          miniImageUrl: (data.miniImageUrl || "").trim() || undefined,
          miniDescription: (data.miniDescription || "").trim() || undefined,
          services: data.services as ServiceType[],
          active: isAdminView ? data.active : true,
          sms_opt_out: isAdminView ? data.sms_opt_out : false, // Include SMS opt-out in add
          latitude: data.latitude,
          longitude: data.longitude,
          radius: data.radius
        });
        toast({
          title: "Επιτυχής προσθήκη!",
          description: "Ο συνεργάτης προστέθηκε επιτυχώς.",
        });
        form.reset({
          name: "",
          phone: "",
          email: "",
          location: "",
          miniImageUrl: "",
          miniDescription: "",
          services: [],
          active: true,
          sms_opt_out: false,
          radius: 10
        });
        
        if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
        
        if (circleRef.current) {
          circleRef.current.setMap(null);
          circleRef.current = null;
        }
      }
      
      setRegistrationComplete(true);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Partner form error:", error);
      toast({
        title: "Σφάλμα",
        description: "Υπήρξε πρόβλημα. Παρακαλώ δοκιμάστε ξανά.",
        variant: "destructive",
      });
    }
  };

  const services = [
    { id: "εξοικονομώ", label: "Εξοικονομώ" },
    { id: "αντλίεςΘερμότητας", label: "Αντλίες Θερμότητας" },
    { id: "φωτοβολταϊκά", label: "Φωτοβολταϊκά" },
    { id: "κλιματιστικά", label: "Κλιματιστικά" },
    { id: "φυσικόΑέριο", label: "Φυσικό Αέριο" },
    { id: "ηλιακά", label: "Ηλιακά" },
    { id: "άλλο", label: "Άλλο" },
  ];

  // If registration is complete, show thank you message
  if (registrationComplete && !isAdminView) {
    return <ThankYouMessage serviceName="Συνεργάτη" />;
  }

  return (
    <Form {...form}>
      <form 
        onSubmit={(e) => {
          // Add extra prevention for form submission
          if (e.nativeEvent instanceof SubmitEvent && 
              e.nativeEvent.submitter === null) {
            // If no explicit submit button was clicked (like Enter key), prevent submission
            e.preventDefault();
            return false;
          }
          form.handleSubmit(
            onSubmit,
            () => {
              toast({
                title: "Δεν αποθηκεύτηκε",
                description: "Ελέγξτε τα πεδία με κόκκινο μήνυμα και δοκιμάστε ξανά.",
                variant: "destructive",
              });
            }
          )(e);
        }} 
        className="space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Card className="animate-fade-in shadow-md hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Βασικά Στοιχεία</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Show Active/Inactive toggle only in admin view */}
                {isAdminView && (
                  <>
                    <FormField
                      control={form.control}
                      name="active"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel>Κατάσταση Συνεργάτη</FormLabel>
                            <div className="text-sm text-muted-foreground">
                              {field.value ? 'Ενεργός' : 'Ανενεργός'}
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    {/* Add SMS Opt-Out toggle for admin view */}
                    <FormField
                      control={form.control}
                      name="sms_opt_out"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel className="flex items-center gap-2">
                              <BellOff className="h-4 w-4" />
                              Απενεργοποίηση SMS
                            </FormLabel>
                            <div className="text-sm text-muted-foreground">
                              {field.value ? 'Δεν θα λαμβάνει SMS' : 'Θα λαμβάνει SMS'}
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </>
                )}
                
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Όνομα Συνεργάτη</FormLabel>
                      <FormControl>
                        <Input placeholder="Όνομα εταιρείας ή ατόμου" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Τηλέφωνο</FormLabel>
                      <FormControl>
                        <Input placeholder="π.χ. 6912345678" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input 
                            placeholder="π.χ. info@example.com" 
                            type="email"
                            {...field} 
                          />
                          <Mail className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Τοποθεσία</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <AddressAutocomplete
                            value={field.value}
                            onChange={field.onChange}
                            onLocationSelect={handleLocationSelect}
                            placeholder="Εισάγετε διεύθυνση ή περιοχή"
                            className="pr-10"
                          />
                          <MapPin className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="miniImageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Εικόνα mini card (URL)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://example.com/partner.jpg" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="miniDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Σύντομη περιγραφή (mini card)</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={4}
                          placeholder="Προσθέστε μια σύντομη περιγραφή που θα εμφανίζεται στη mini σελίδα συνεργάτη."
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isAdminView && (
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <p className="text-sm text-slate-600">Προεπισκόπηση mini page συνεργάτη</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!initialData?.id}
                        onClick={() => {
                          if (!initialData?.id) return;
                          window.open(`/mini-preview/${initialData.id}`, "_blank", "noopener,noreferrer");
                        }}
                      >
                        Προβολή Mini Page
                      </Button>
                    </div>
                    {!initialData?.id && (
                      <p className="text-xs text-slate-500 mt-2">Το preview ενεργοποιείται μετά την αποθήκευση του συνεργάτη.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="animate-fade-in shadow-md hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Υπηρεσίες</CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="services"
                  render={() => (
                    <FormItem>
                      <div className="mb-4">
                        <FormLabel>Επιλέξτε τις υπηρεσίες που προσφέρετε</FormLabel>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {services.map((service) => (
                          <FormField
                            key={service.id}
                            control={form.control}
                            name="services"
                            render={({ field }) => {
                              return (
                                <FormItem
                                  key={service.id}
                                  className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3"
                                >
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(service.id)}
                                      onCheckedChange={(checked) => {
                                        return checked
                                          ? field.onChange([...field.value, service.id])
                                          : field.onChange(
                                              field.value?.filter(
                                                (value) => value !== service.id
                                              )
                                            );
                                      }}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {service.label}
                                  </FormLabel>
                                </FormItem>
                              );
                            }}
                          />
                        ))}
                      </div>
                      <FormMessage className="mt-2" />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="animate-fade-in shadow-md hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Περιοχή Κάλυψης</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div 
                  ref={mapRef} 
                  className="w-full h-[300px] rounded-md border border-gray-300 bg-gray-50"
                ></div>
                <FormField
                  control={form.control}
                  name="radius"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col space-y-1">
                        <FormLabel>Ακτίνα εξυπηρέτησης: {field.value} χλμ</FormLabel>
                        <FormControl>
                          <Slider
                            min={1}
                            max={100}
                            step={1}
                            defaultValue={[field.value]}
                            onValueChange={(values) => {
                              field.onChange(values[0]);
                              updateRadius(values);
                            }}
                            className="w-full"
                          />
                        </FormControl>
                        <p className="text-sm text-muted-foreground">
                          Επιλέξτε σημείο στον χάρτη και προσαρμόστε την ακτίνα εξυπηρέτησης
                        </p>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        
        <Button type="submit" className="w-full bg-primary hover:bg-primary/90 transition-colors">
          {isAdminView ? (initialData?.id ? "Ενημέρωση" : "Προσθήκη") : "Εγγραφή"}
        </Button>
      </form>
    </Form>
  );
};

export default PartnerForm;
